import { useNavigate, useLocation } from 'react-router-dom';
import { useUiStore } from '../store/ui';
import { useTranslation } from 'react-i18next';
import {
  COLORS,
  WALLET_COLORS as C,
  DRAWER_COLORS,
  DRAWER_LANGUAGES,
  DRAWER_MENU_ITEMS,
  filterProdRoutes,
  type DrawerItem,
  type DrawerLanguageCode,
} from '@/constants';

// ============================================================================
// TYPES
// ============================================================================

type DrawerHeaderProps = {
  onClose: () => void;
};

type MenuItemProps = {
  item: DrawerItem;
  isActive: boolean;
  isFirst: boolean;
  onClick: () => void;
};

type MenuListProps = {
  currentPath: string;
  onItemClick: (to?: string) => void;
};

type LanguageSelectorProps = {
  currentLang: string;
  onLanguageChange: (lang: DrawerLanguageCode) => void;
};

type LanguageButtonProps = {
  label: string;
  isSelected: boolean;
  onClick: () => void;
};

type BackdropProps = {
  show: boolean;
  onClick: () => void;
};

// ============================================================================
// COMPONENTS
// ============================================================================

// Logo with glow effect
function LogoWithGlow() {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-center gap-1">
      <img src="/orbit.png" alt="" className="size-8 object-cover" draggable={false} />
      <div className="flex align-center gap-1 text-base font-semibold leading-none">
        <span className="bg-clip-text text-transparent" style={{ backgroundImage: C.logoText }}>ORB</span>
        <span style={{ color: COLORS.iconGlobal }}>{t('auth.orbTitle')}</span>
      </div>
    </div>
  );
}

// Close button icon
function CloseIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
      <path fillRule="evenodd" clipRule="evenodd" d="M14.9073 2.84663C15.256 2.58792 15.7513 2.61637 16.0675 2.93256C16.4153 3.28037 16.4153 3.84452 16.0675 4.19233L10.7599 9.49995L16.0675 14.8076C16.4153 15.1554 16.4153 15.7195 16.0675 16.0673C15.7513 16.3835 15.256 16.412 14.9073 16.1533L14.8077 16.0673L9.50012 10.7597L4.1925 16.0673L4.09289 16.1533C3.74423 16.412 3.24892 16.3835 2.93273 16.0673C2.58492 15.7195 2.58492 15.1554 2.93273 14.8076L8.24035 9.49995L2.93273 4.19233C2.58492 3.84452 2.58492 3.28037 2.93273 2.93256C3.24892 2.61637 3.74423 2.58792 4.09289 2.84663L4.1925 2.93256L9.50012 8.24018L14.8077 2.93256L14.9073 2.84663Z" fill={DRAWER_COLORS.iconClose}/>
    </svg>
  );
}

// Drawer header
function DrawerHeader({ onClose }: DrawerHeaderProps) {
  return (
    <div
      className="flex items-center justify-between border-b px-4 py-3"
      style={{ borderColor: DRAWER_COLORS.borderDrawer }}
    >
      <div className="flex items-center gap-3">
        <LogoWithGlow />
      </div>
      <button
        type="button"
        onClick={onClose}
        className="flex h-8 w-8 items-center justify-center rounded-lg border text-text-secondary hover:text-text-primary active:bg-surface-overlay transition-colors"
        style={{ background: DRAWER_COLORS.bgDrawer, borderColor: DRAWER_COLORS.borderDrawer }}
      >
        <CloseIcon />
      </button>
    </div>
  );
}

// Menu item
function MenuItem({ item, isActive, isFirst, onClick }: MenuItemProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!item.to}
      className={`flex w-full items-center gap-3.5 px-3 py-2.5 text-left transition-all rounded-xl border ${
        !isFirst ? 'mt-1' : ''
      } ${item.to ? 'text-text-primary' : 'text-text-muted cursor-not-allowed'}`}
      style={{
        background: isActive ? DRAWER_COLORS.bgDrawer : 'transparent',
        borderColor: isActive ? DRAWER_COLORS.borderDrawer : 'transparent',
      }}
    >
      <img src={item.icon} alt="" className="w-6 h-6" />
      <span className="text-sm font-medium">{t(item.labelKey)}</span>
      {!item.to && (
        <span className="ml-auto rounded bg-[#1a2332] px-1.5 py-0.5 text-[9px] text-text-muted">
          {t('menu.coming_soon')}
        </span>
      )}
    </button>
  );
}

// Menu divider
function MenuDivider() {
  return <div className="h-px w-full my-1" style={{ background: DRAWER_COLORS.borderHeader }} />;
}

// Menu list
function MenuList({ currentPath, onItemClick }: MenuListProps) {
  const menuItems = filterProdRoutes(DRAWER_MENU_ITEMS);

  return (
    <nav className="flex-1 overflow-y-auto px-4 py-3 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
      {menuItems.map((item, idx) => {
        const isActive = item.to === currentPath;
        const isLast = idx === menuItems.length - 1;

        return (
          <div key={item.labelKey}>
            <MenuItem
              item={item}
              isActive={isActive}
              isFirst={idx === 0}
              onClick={() => onItemClick(item.to)}
            />
            {!isLast && <MenuDivider />}
          </div>
        );
      })}
    </nav>
  );
}

// Language selector
function LanguageSelector({ currentLang, onLanguageChange }: LanguageSelectorProps) {
  const isZh = currentLang === 'zh' || currentLang === 'zh-TW';
  const isSelected = (code: DrawerLanguageCode) => code === 'zh' ? isZh : !isZh;

  return (
    <div className="px-4 py-3">
      <div
        className="flex items-center justify-between gap-2 py-2 px-1.5 rounded-xl"
        style={{ border: `1px solid ${DRAWER_COLORS.borderDrawer}`, background: DRAWER_COLORS.bgLanguage }}
      >
        <div className="flex items-center gap-2">
          <img src="/globe.png" alt="Language" className="w-[30px]" />
          <span className="text-[10px]" style={{ color: DRAWER_COLORS.languageText }}>
            语言/Language
          </span>
        </div>
        <div
          className="flex rounded-[10px] overflow-hidden text-[10px]"
          style={{ border: `1px solid ${DRAWER_COLORS.borderDrawer}` }}
        >
          {DRAWER_LANGUAGES.map((language) => (
            <LanguageButton
              key={language.code}
              label={language.label}
              isSelected={isSelected(language.code)}
              onClick={() => onLanguageChange(language.code)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Language button
function LanguageButton({ label, isSelected, onClick }: LanguageButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-2 font-medium transition-colors rounded-[10px]"
      style={{
        background: isSelected ? DRAWER_COLORS.bgDrawer : 'transparent',
        color: isSelected ? DRAWER_COLORS.headerText : DRAWER_COLORS.languageText,
        border: `1px solid ${isSelected ? DRAWER_COLORS.borderDrawer : 'transparent'}`,
      }}
    >
      {label}
    </button>
  );
}

// Backdrop overlay
function Backdrop({ show, onClick }: BackdropProps) {
  if (!show) return null;
  return (
    <div className="absolute inset-0 z-[60] bg-black/60 backdrop-blur-sm" onClick={onClick} />
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export function SideDrawer() {
  const open = useUiStore((s) => s.drawerOpen);
  const close = useUiStore((s) => s.closeDrawer);
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  const handleMenuClick = (to?: string) => {
    close();
    if (to) navigate(to);
  };

  const handleLanguageChange = (lang: DrawerLanguageCode) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('uo_lang', lang);
  };

  return (
    <>
      <Backdrop show={open} onClick={close} />

      <div
        className={`absolute top-2 z-[70] h-[calc(100%-16px)] w-[260px] shadow-2xl
          flex flex-col transition-all duration-300 ease-in-out border rounded-[23px]
          ${open ? 'left-2 translate-x-0 pointer-events-auto' : 'left-0 -translate-x-full pointer-events-none'}`}
        style={{
          background: DRAWER_COLORS.bgDrawer,
          boxShadow: DRAWER_COLORS.shadowDrawer,
          borderColor: DRAWER_COLORS.borderDrawer,
          backdropFilter: "blur(2px)",
        }}
      >
        <DrawerHeader onClose={close} />
        <MenuList currentPath={location.pathname} onItemClick={handleMenuClick} />
        <div className="h-px w-full" style={{ background: DRAWER_COLORS.divider }} />
        <LanguageSelector currentLang={i18n.language} onLanguageChange={handleLanguageChange} />
      </div>
    </>
  );
}
